import { getDateDayPlusTwo, getDateMonthMinusOne } from '../../utils/dates';
import { types } from '../types/types';

const { year, month, day } = getDateDayPlusTwo();
const { yearY, monthY, dayY } = getDateMonthMinusOne();
const SALES_LIST_STATE_KEY = 'salesListState';

const defaultInvoicesState = {
  data: [],
  count: 0,
  page: 0,
  sum: 0,
  currentFilter: [],
  dateRange: [new Date(yearY, monthY, dayY).toISOString(), new Date(year, month, day).toISOString()],
  dateFilterField: 'FechaVenta',
};

const getPersistedInvoicesState = () => {
  if (typeof window === 'undefined') return defaultInvoicesState;

  const rawState = localStorage.getItem(SALES_LIST_STATE_KEY);
  if (!rawState) return defaultInvoicesState;

  try {
    const parsedState = JSON.parse(rawState);
    return {
      ...defaultInvoicesState,
      currentFilter: Array.isArray(parsedState?.currentFilter) ? parsedState.currentFilter : defaultInvoicesState.currentFilter,
      dateRange:
        Array.isArray(parsedState?.dateRange) && parsedState.dateRange.length === 2
          ? parsedState.dateRange
          : defaultInvoicesState.dateRange,
      dateFilterField:
        typeof parsedState?.dateFilterField === 'string'
          ? parsedState.dateFilterField
          : defaultInvoicesState.dateFilterField,
      page: Number.isInteger(parsedState?.page) && parsedState.page >= 0 ? parsedState.page : defaultInvoicesState.page,
    };
  } catch (error) {
    localStorage.removeItem(SALES_LIST_STATE_KEY);
    return defaultInvoicesState;
  }
};

const persistInvoicesState = invoicesState => {
  if (typeof window === 'undefined') return;

  const stateToPersist = {
    currentFilter: invoicesState.currentFilter,
    dateRange: invoicesState.dateRange,
    dateFilterField: invoicesState.dateFilterField,
    page: invoicesState.page,
  };

  localStorage.setItem(SALES_LIST_STATE_KEY, JSON.stringify(stateToPersist));
};

const clearPersistedInvoicesState = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SALES_LIST_STATE_KEY);
};

const createInitialState = () => ({
  invoices: getPersistedInvoicesState(),
  active: {
    orderLines: [],
    activeLine: null,
    services: [],
    supplier: null,
    rate: null,
  },
  activeEdit: null,
});

const initialState = createInitialState();

export const invoiceReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.changeFilter: {
      const invoices = {
        ...state.invoices,
        currentFilter: action.payload,
      };
      persistInvoicesState(invoices);
      return {
        ...state,
        invoices,
      };
    }
    case types.changeFilterDates: {
      const invoices = {
        ...state.invoices,
        dateRange: action.payload,
      };
      persistInvoicesState(invoices);
      return {
        ...state,
        invoices,
      };
    }
    case types.changeFilterField: {
      const invoices = {
        ...state.invoices,
        dateFilterField: action.payload,
      };
      persistInvoicesState(invoices);
      return {
        ...state,
        invoices,
      };
    }
    case types.invoiceActive:
      return {
        ...state,
        active: {
          ...action.payload,
        },
      };
    case types.linesAddNew:
      return {
        ...state,
        active: {
          ...state.active,
          orderLines: [...state.active.orderLines, action.payload],
        },
      };
    case types.lineDelete:
      return {
        ...state,
        active: {
          ...state.active,
          orderLines: state.active.orderLines.filter((orderLine, index) => index !== action.payload),
        },
      };
    case types.invoicesLoad: {
      const invoices = {
        ...state.invoices,
        data: [...action.payload],
        page: action.page,
      };
      persistInvoicesState(invoices);
      return {
        ...state,
        activeEdit: null,
        invoices,
      };
    }

    case types.invoiceDelete:
      return {
        ...state,
        active: null,
        invoices: state.invoices.filter(invoice => invoice.PkVentas !== action.payload),
      };
    case types.invoiceActiveEdit:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          data: [],
        },
        activeEdit: {
          ...action.payload,
        },
      };
    case types.invoiceShare:
      return {
        ...state,
        invoices: {
          ...state.invoices,
        },
        activeEdit: {
          ...action.payload,
        },
      };
    case types.lineDeleteFromInvoice:
      return {
        ...state,
        activeEdit: {
          ...state.activeEdit,
          LineasVentaCompuestas: state.activeEdit.LineasVentaCompuestas.filter(
            line => line.PkLineasVenta !== action.payload,
          ),
        },
      };

    case types.getNumberOfSInvoices:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          count: action.payload,
        },
      };
    case types.getTotalInvoicesFiltered:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          sum: action.payload.sum,
          sumInSituEuros: action.payload.sumInSituEuros,
          sumReservaEuros: action.payload.sumReservaEuros,
          sumDescuentoEuros: action.payload.sumDescuentoEuros,
          sumReventaEuros: action.payload.sumReventaEuros,
          sumBalanceDolares: action.payload.sumBalanceDolares,
        },
      };
    case types.resetPage: {
      const invoices = {
        ...state.invoices,
        page: 0,
      };
      persistInvoicesState(invoices);
      return {
        ...state,
        invoices,
      };
    }
    case types.lineEditFromInvoiceState:
      return {
        ...state,
        active: {
          ...state.active,
          activeLine: {
            ...action.payload,
          },
          services: [],
          supplier: null,
        },
      };
    case types.orderLineEditStateLoadingSuppliersAndServices:
      return {
        ...state,
        active: {
          ...state.active,
          services: [...action.payload[0]],
          supplier: {
            ...action.payload[1],
          },
        },
      };
    case types.orderLineStateSetExchangerate:
      return {
        ...state,
        active: {
          ...state.active,
          rate: {
            ...action.payload,
          },
        },
      };
    case types.resetActiveLineFromState:
      return {
        ...state,
        active: {
          ...state.active,
          activeLine: {
            ...state.active.activeLine,
            FkServiciosProveedores: action.payload.PkServiciosProveedores,
            InSituLineaDolares: action.payload.CosteDolares,
            Concepto: action.payload.Nombre,
            ReservaLineaEuros: action.payload.ReservaEuros,
            FkTiposCambioProveedores: action.rate,
            CosteDolares: action.payload.CosteDolares,
            ReservaEuros: action.payload.ReservaEuros,
            NombreServicio: action.payload.Nombre,
            FkTiposServicio: action.payload.FkTiposServicio,
            TasaDolares: action.payload.TasaDolares,
          },
        },
      };
    case types.lineUpdateState:
      return {
        ...state,
        active: {
          ...state.active,
          orderLines: state.active.orderLines.map((orderLine, i) =>
            i == action.payload.id ? action.payload.line : orderLine,
          ),
        },
      };
    case types.invoiceLineStateLogOut:
      return {
        ...state,
        active: {
          ...state.active,
          activeLine: null,
          services: [],
          supplier: null,
          rate: null,
        },
      };

    case types.invoiceDeletebyId:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          data: state.invoices.data.filter(invoice => invoice.PkVentas !== action.payload),
        },
      };

    case types.invoicesLogOutCleaning:
      clearPersistedInvoicesState();
      return createInitialState();

    default:
      return state;
  }
};
